
import React, { useState } from 'react';
import {
  Shield, Bot, User, Cpu, AlertTriangle,
  CheckCircle2, Search, Filter, Download,
  ExternalLink, Fingerprint, Lock, ShieldCheck, Clock, Send, Sparkles, X, Loader2
} from 'lucide-react';
import { LogEntry } from '../types';
import { askGDPRSpecialist } from '../services/geminiService';

const MOCK_LOGS: LogEntry[] = [
  { id: 'log-1', timestamp: '2024-03-15 14:32:01', actor: 'Froukje (AI)', actorType: 'AI', action: 'Gegenereerd marktonderzoek en persona', entity: 'Vacature: Senior Dev', severity: 'INFO' },
  { id: 'log-2', timestamp: '2024-03-15 15:10:45', actor: 'Thomas de Vries', actorType: 'HUMAN', action: 'Handmatige aanpassing persona doelen', entity: 'Vacature: Senior Dev', severity: 'INFO' },
  { id: 'log-3', timestamp: '2024-03-15 16:05:12', actor: 'Systeem', actorType: 'SYSTEM', action: 'Campagne automatisch gestart op LinkedIn', entity: 'Marketing Flow', severity: 'INFO' },
  { id: 'log-4', timestamp: '2024-03-16 09:21:00', actor: 'Froukje (AI)', actorType: 'AI', action: '9 kandidaten gesegmenteerd naar Talent Pool', entity: 'Leadgen 90/10', severity: 'INFO' },
  { id: 'log-5', timestamp: '2024-03-16 10:45:30', actor: 'Linda Veenstra', actorType: 'HUMAN', action: 'Kandidaat Jasper v.d. Berg verplaatst naar 1e Gesprek', entity: 'Kanban Flow', severity: 'INFO' },
  { id: 'log-6', timestamp: '2024-03-16 11:00:00', actor: 'Systeem', actorType: 'SYSTEM', action: 'Fout bij verzenden SMS template (Timeout)', entity: 'Automation Flow', severity: 'WARNING' },
  { id: 'log-7', timestamp: '2024-03-16 13:15:22', actor: 'Thomas de Vries', actorType: 'HUMAN', action: 'Abonnement gewijzigd naar Hybrid Growth', entity: 'Subscription Management', severity: 'CRITICAL' },
];

const LogsManager: React.FC = () => {
  const [filter, setFilter] = useState<'ALL' | 'AI' | 'HUMAN'>('ALL');
  const [search, setSearch] = useState('');
  const [dateSearch, setDateSearch] = useState('');

  // GDPR Chat State
  const [showGDPRChat, setShowGDPRChat] = useState(false);
  const [gdprMessages, setGdprMessages] = useState<{ role: 'user' | 'model', content: string }[]>([]);
  const [gdprInput, setGdprInput] = useState('');
  const [isGdprLoading, setIsGdprLoading] = useState(false);

  const filteredLogs = MOCK_LOGS.filter(log => {
    const matchesFilter = filter === 'ALL' || log.actorType === filter;
    const matchesSearch = log.action.toLowerCase().includes(search.toLowerCase()) ||
      log.actor.toLowerCase().includes(search.toLowerCase());
    const matchesDate = !dateSearch || log.timestamp.substring(0, 10) === dateSearch;
    return matchesFilter && matchesSearch && matchesDate;
  });

  return (
    <div className="space-y-8 pb-20 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
            <ShieldCheck className="text-[#e51b23] shrink-0" size={24} />
            GDPR Audit Trail
          </h1>
          <p className="text-xs text-slate-500 font-medium mt-1">AVG & Privacy Compliance Dashboard</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowGDPRChat(true)}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg flex items-center gap-2 text-xs font-black uppercase tracking-widest hover:bg-black transition-all shadow-lg"
          >
            <Bot size={14} /> AI Scan
          </button>
          <div className="px-4 py-2 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-lg flex items-center gap-2 text-xs font-black uppercase tracking-widest shadow-sm">
            <Fingerprint size={14} /> Immutable Storage
          </div>
        </div>
      </div>

      {/* Control Bar */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex bg-white p-1.5 rounded-lg border border-slate-200 shadow-sm w-full md:w-auto">
          {['ALL', 'AI', 'HUMAN'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f as any)}
              className={`flex-1 md:flex-none px-6 py-2 rounded-md text-[10px] font-black tracking-widest uppercase transition-all ${filter === f ? 'bg-red-600 text-white shadow-lg' : 'text-slate-400 hover:text-red-600'
                }`}
            >
              {f === 'ALL' ? 'Totaal' : f === 'AI' ? 'AI Agent' : 'Menselijk'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative">
            <input
              type="date"
              value={dateSearch}
              onChange={(e) => setDateSearch(e.target.value)}
              className="pl-3 pr-4 py-3 bg-white border border-slate-200 rounded-md text-[10px] font-black uppercase tracking-widest outline-none focus:ring-4 focus:ring-red-500/5 transition-all text-slate-600 h-[42px]"
              title="Zoek op datum"
            />
          </div>
          <div className="relative flex-1 md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Zoek in audit trail..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-md text-[10px] font-black uppercase tracking-widest outline-none focus:ring-4 focus:ring-red-500/5 transition-all h-[42px]"
            />
          </div>
          <button className="flex items-center gap-2 px-4 py-3 bg-white border border-slate-200 rounded-md text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 transition-all shadow-sm h-[42px]">
            <Download size={16} /> Export PDF
          </button>
        </div>
      </div>

      {/* COMPACT DATA TABLE */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden relative">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500">
                <th className="p-4 w-48">Timestamp / Date</th>
                <th className="p-4 w-24">Type</th>
                <th className="p-4">Actor</th>
                <th className="p-4 w-56">Entity / Context</th>
                <th className="p-4">Action Message</th>
                <th className="p-4 text-right">Log ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredLogs.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50/80 transition-colors group">
                  <td className="p-4">
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-900">{log.timestamp.split(' ')[0]}</span>
                      <span className="text-[10px] text-slate-400 font-mono font-medium flex items-center gap-1">
                        <Clock size={10} /> {log.timestamp.split(' ')[1]}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    {log.actorType === 'AI' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-600 rounded text-[9px] font-black uppercase tracking-wider border border-red-100">
                        <Bot size={10} /> AI
                      </span>
                    )}
                    {log.actorType === 'HUMAN' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-600 rounded text-[9px] font-black uppercase tracking-wider border border-emerald-100">
                        <User size={10} /> Mens
                      </span>
                    )}
                    {log.actorType === 'SYSTEM' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded text-[9px] font-black uppercase tracking-wider border border-slate-200">
                        <Cpu size={10} /> Sys
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <span className="font-bold text-slate-800">{log.actor}</span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Shield size={14} className="text-slate-400" />
                      <span className="text-xs font-bold text-slate-600">{log.entity}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <span className={`w-1.5 h-1.5 rounded-full ${log.severity === 'WARNING' ? 'bg-amber-500' :
                        log.severity === 'CRITICAL' ? 'bg-rose-500' :
                          'bg-slate-300'
                        }`} />
                      <span className={`text-xs font-medium whitespace-normal line-clamp-2 ${log.severity === 'WARNING' ? 'text-amber-800' :
                        log.severity === 'CRITICAL' ? 'text-rose-800' :
                          'text-slate-700'
                        }`}>{log.action}</span>
                    </div>
                  </td>
                  <td className="p-4 text-right">
                    <span className="text-[10px] font-mono font-bold text-slate-400 flex items-center justify-end gap-1 group-hover:text-slate-600 transition-colors">
                      <Fingerprint size={12} /> {log.id.toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredLogs.length === 0 && (
          <div className="py-24 text-center">
            <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-100 text-slate-300">
              <Search size={32} />
            </div>
            <p className="text-slate-400 font-bold">Geen log-entries gevonden.</p>
          </div>
        )}

        <div className="p-8 bg-slate-50 border-t border-slate-100 flex items-center justify-center gap-4">
          <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
            <CheckCircle2 size={14} className="text-emerald-500" />
            Audit Trail Verified by Werftools Secure OS
          </div>
        </div>
      </div>

      {/* GDPR Chat Modal */}
      {showGDPRChat && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white w-full max-w-2xl h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-scaleIn border border-slate-200">

            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-600 to-rose-600 flex items-center justify-center text-white shadow-md">
                  <ShieldCheck size={24} />
                </div>
                <div className="leading-tight mt-1">
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight flex items-center gap-2">Werfplaats AI <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded tracking-wide">BETA</span></h3>
                  <p className="text-[10px] text-slate-500 font-medium">AVG / GDPR Compliance AI</p>
                </div>
              </div>
              <button
                onClick={() => setShowGDPRChat(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-white custom-scrollbar">
              {gdprMessages.length === 0 && (
                <div className="text-center py-10 max-w-md mx-auto">
                  <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-red-100">
                    <ShieldCheck size={28} className="text-red-500" />
                  </div>
                  <h4 className="text-sm font-bold text-slate-900 mb-2">Kan ik je helpen met compliance?</h4>
                  <p className="text-xs text-slate-600 leading-relaxed font-medium">
                    Welkom! Ik ben de AVG compliance specialist.
                    Ik kan de huidige audit logs scannen op mogelijke AVG/GDPR overtredingen, toestemmingen controleren en adviseren over rechtmatige gegevensverwerking.
                  </p>
                  <div className="flex flex-col gap-2">
                    <button onClick={() => { setGdprInput("Toon mij de privacy risico's in deze logs"); }} className="text-xs text-left p-3 rounded-lg border border-slate-200 hover:border-red-300 hover:bg-red-50 transition-colors">🔥 Toon mij de privacy risico's</button>
                    <button onClick={() => { setGdprInput("Welke AVG regels gelden voor de verwerking van CV's?"); }} className="text-xs text-left p-3 rounded-lg border border-slate-200 hover:border-red-300 hover:bg-red-50 transition-colors">📑 AVG regels voor CV verwerking?</button>
                  </div>
                </div>
              )}

              {gdprMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl p-4 text-sm leading-relaxed ${msg.role === 'user'
                    ? 'bg-slate-900 text-white rounded-tr-sm'
                    : 'bg-slate-50 text-slate-700 border border-slate-100 rounded-tl-sm'
                    }`}>
                    {msg.role === 'model' ? (
                      <div dangerouslySetInnerHTML={{ __html: msg.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }} />
                    ) : (
                      <p>{msg.content}</p>
                    )}
                  </div>
                </div>
              ))}

              {isGdprLoading && (
                <div className="flex justify-start">
                  <div className="bg-slate-50 border border-slate-100 rounded-2xl rounded-tl-sm p-4 flex items-center gap-3 text-slate-500 text-sm">
                    <Loader2 size={16} className="animate-spin text-red-500" /> Analyseren van logs...
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-4 bg-white border-t border-slate-100">
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={gdprInput}
                  onChange={(e) => setGdprInput(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && !isGdprLoading && gdprInput.trim()) {
                      const query = gdprInput.trim();
                      setGdprInput('');

                      const newHistory = [...gdprMessages, { role: 'user' as const, content: query }];
                      setGdprMessages(newHistory);
                      setIsGdprLoading(true);

                      const response = await askGDPRSpecialist(query, filteredLogs, gdprMessages);

                      setGdprMessages([...newHistory, { role: 'model', content: response }]);
                      setIsGdprLoading(false);
                    }
                  }}
                  placeholder="Vraag iets aan de GDPR specialist..."
                  className="w-full pl-4 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-red-400 focus:ring-4 focus:ring-red-50 transition-all"
                  disabled={isGdprLoading}
                />
                <button
                  onClick={async () => {
                    if (!isGdprLoading && gdprInput.trim()) {
                      const query = gdprInput.trim();
                      setGdprInput('');

                      const newHistory = [...gdprMessages, { role: 'user' as const, content: query }];
                      setGdprMessages(newHistory);
                      setIsGdprLoading(true);

                      const response = await askGDPRSpecialist(query, filteredLogs, gdprMessages);

                      setGdprMessages([...newHistory, { role: 'model', content: response }]);
                      setIsGdprLoading(false);
                    }
                  }}
                  className={`absolute right-2 p-2 rounded-lg transition-colors ${gdprInput.trim() && !isGdprLoading ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-slate-200 text-slate-400'
                    }`}
                  disabled={!gdprInput.trim() || isGdprLoading}
                >
                  <Send size={16} />
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default LogsManager;
